import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, Check, Clock, Mail, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../lib/errors";
import { Collapsible, VOLUNTEER_HOLD_HARMLESS_TITLE, VolunteerHoldHarmless } from "./legal/HoldHarmless";

const VolunteerForm = ({ campaignId = null, compact = false }) => {
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", availability: "", message: "" });
    const [portalLinkState, setPortalLinkState] = useState("idle"); // idle | sending | sent

    const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!accepted) {
            toast.error("Please acknowledge the volunteer hold-harmless to continue.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/volunteers", { ...form, campaign_id: campaignId, hold_harmless_accepted: true });
            setDone(true);
            toast.success("Thanks for signing up to volunteer!");
        } catch (err) {
            toast.error(formatApiError(err) || "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    const requestPortalLink = async () => {
        setPortalLinkState("sending");
        try {
            await api.post("/volunteer-portal/request", { email: form.email });
            setPortalLinkState("sent");
        } catch (err) {
            toast.error(formatApiError(err) || "Couldn't send link");
            setPortalLinkState("idle");
        }
    };

    if (done) {
        return (
            <div className="bg-white border border-[#E53935]/30 rounded-2xl p-8" data-testid="volunteer-success">
                <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-[#F2D8D5] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-[#E53935]" strokeWidth={2} />
                    </div>
                    <p className="font-heading text-xl text-[#1C252E] font-medium mb-2">You&apos;re in!</p>
                    <p className="text-sm text-[#4A5568]">Our team will reach out within 48 hours with next steps.</p>
                </div>

                {/* Optional dashboard upgrade */}
                <div className="mt-6 pt-6 border-t border-black/5" data-testid="volunteer-portal-upsell">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-[#F4F1EA] flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4 text-[#1C252E]" strokeWidth={1.75} />
                        </div>
                        <div>
                            <p className="font-heading text-base text-[#1C252E] font-medium">Optional · Track your service hours</p>
                            <p className="text-xs text-[#4A5568] leading-relaxed mt-0.5">
                                We can email you a private link to a lightweight dashboard where you can log hours and download a service-hour certificate. No password, no account needed.
                            </p>
                        </div>
                    </div>

                    {portalLinkState === "sent" ? (
                        <div className="bg-[#F4F1EA] rounded-xl px-4 py-3 flex items-start gap-2" data-testid="volunteer-portal-sent">
                            <Mail className="w-4 h-4 text-[#4A5568] flex-shrink-0 mt-0.5" strokeWidth={2} />
                            <p className="text-xs text-[#4A5568] leading-relaxed">
                                Sent! Check <strong className="text-[#1C252E]">{form.email}</strong> for your dashboard link (valid 14 days).
                            </p>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={requestPortalLink}
                            disabled={portalLinkState === "sending"}
                            data-testid="volunteer-portal-request-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#1C252E] hover:bg-black text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-60"
                        >
                            {portalLinkState === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            {portalLinkState === "sending" ? "Sending…" : "Email me a dashboard link"}
                        </button>
                    )}
                    <p className="text-[11px] text-[#4A5568] mt-2 text-center">
                        Already have a link? <Link to="/volunteer-portal" className="text-[#E53935] underline">Open the dashboard.</Link>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#F2D8D5] flex items-center justify-center">
                    <HandHeart className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                </div>
                <div>
                    <p className="font-heading text-xl text-[#1C252E] font-medium">Volunteer with us</p>
                    <p className="text-xs text-[#4A5568]">Lend your time, talents, or treats — we&apos;ll reach out within 48 hours.</p>
                </div>
            </div>

            {/* Contact */}
            <div className="grid sm:grid-cols-2 gap-3">
                <VLbl label="Full name *">
                    <input required name="name" aria-label="Full name" autoComplete="name" value={form.name} onChange={change} data-testid="volunteer-name" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" />
                </VLbl>
                <VLbl label="Email *">
                    <input required type="email" name="email" aria-label="Email" autoComplete="email" value={form.email} onChange={change} data-testid="volunteer-email" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" />
                </VLbl>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <VLbl label="Phone" hint="Optional — only used if we can't reach you by email.">
                    <input name="phone" aria-label="Phone number (optional)" autoComplete="tel" value={form.phone} onChange={change} data-testid="volunteer-phone" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" />
                </VLbl>
                <VLbl label="How would you like to help?">
                    <select name="role" aria-label="How would you like to help?" value={form.role} onChange={change} data-testid="volunteer-role" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                        <option value="">— Pick a role —</option>
                        <option>Event setup & breakdown</option>
                        <option>Chaperone / classroom helper</option>
                        <option>Bake sale / food</option>
                        <option>Fundraiser coordinator</option>
                        <option>Marketing & social media</option>
                        <option>Wherever you need me</option>
                    </select>
                </VLbl>
            </div>

            {/* Availability + freeform — hidden in compact mode */}
            {!compact && (
                <VLbl label="Availability" hint="When can you typically help? e.g. weekday mornings, weekends.">
                    <input name="availability" aria-label="Availability" value={form.availability} onChange={change} data-testid="volunteer-availability" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" />
                </VLbl>
            )}
            <VLbl label="Anything else we should know?">
                <textarea name="message" aria-label="Additional message (optional)" value={form.message} onChange={change} rows={3} data-testid="volunteer-message" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white resize-none" />
            </VLbl>

            <Collapsible title={VOLUNTEER_HOLD_HARMLESS_TITLE} testid="volunteer-hh-collapsible">
                <VolunteerHoldHarmless />
            </Collapsible>

            <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl bg-[#F4F1EA] border border-black/5 px-4 py-3" data-testid="volunteer-hh-label">
                <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    required
                    data-testid="volunteer-hh-checkbox"
                    className="mt-1 w-4 h-4 rounded border-black/30 text-[#E53935] focus:ring-[#E53935] flex-shrink-0"
                />
                <span className="text-xs text-[#1C252E] leading-relaxed">
                    I have read and agree to the <strong>Volunteer Acknowledgment, Release, and Hold Harmless</strong>. I understand I am not an employee of the foundation or any school district.
                </span>
            </label>

            <button
                type="submit"
                disabled={submitting || !accepted}
                data-testid="volunteer-submit-btn"
                className="w-full bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full py-3 font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {submitting ? "Sending…" : "Sign me up"}
            </button>
        </form>
    );
};

// Tiny labeled-field wrapper for the public volunteer form. Mirrors the
// `Lbl` helper used by /apply/contractor and /apply/grant so the public
// forms feel consistent.
const VLbl = ({ label, hint, children }) => (
    <div>
        <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-[#4A5568] mt-1.5">{hint}</p>}
    </div>
);

export default VolunteerForm;
